import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

function AuthModal({ isOpen, onClose, mode: initialMode = 'login' }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  // Sync mode with prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      clearForm();
    }
  }, [isOpen, initialMode]);

  const clearForm = () => {
    setIdentifier('');
    setUsername('');
    setEmail('');
    setBirthdate('');
    setPassword('');
    setError('');
    setSuccess('');
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const userData = await login(identifier, password);
        onClose();
        // First login: redirect to profile
        if (userData && !localStorage.getItem('hasLoggedInBefore')) {
          localStorage.setItem('hasLoggedInBefore', 'true');
          navigate('/profile');
        }
      } else if (mode === 'forgot') {
        // Request password reset
        const res = await fetch('/api/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Fehler');
        setSuccess('📧 Reset-Link wurde an deine E-Mail gesendet!');
      } else {
        // Register and redirect to email verification
        const result = await register(username, email, birthdate, password);
        localStorage.setItem('registrationEmail', email);
        onClose();
        navigate('/verify-email');
      }
    } catch (err) {
      // Handle EMAIL_NOT_VERIFIED error from login
      if (err.message === 'Email not verified') {
        setError('E-Mail noch nicht verifiziert. Prüfe deinen Posteingang.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    clearForm();
  };

  const getTitle = () => {
    if (mode === 'forgot') return 'Passwort vergessen';
    return mode === 'login' ? 'Anmelden' : 'Registrieren';
  };

  const getSubmitLabel = () => {
    if (loading) return 'Lädt...';
    if (mode === 'forgot') return 'Reset-Link senden';
    return mode === 'login' ? 'Anmelden' : 'Registrieren';
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>✕</button>
        
        <h2 className="auth-modal-title">{getTitle()}</h2>

        {success && <div className="auth-success">{success}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'login' ? (
            <>
              <div className="auth-form-group">
                <label>Username oder E-Mail</label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="auth-form-group">
                <label>Passwort</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="auth-forgot-link">
                <button type="button" onClick={() => { setMode('forgot'); clearForm(); }} className="auth-switch-btn">
                  Passwort vergessen?
                </button>
              </div>
            </>
          ) : mode === 'forgot' ? (
            <>
              <p className="auth-info-text">
                Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.
              </p>
              <div className="auth-form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </>
          ) : (
            <>
              <div className="auth-form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  pattern="[a-zA-Z0-9._-]{3,30}"
                  title="3-30 Zeichen: Buchstaben, Zahlen, . _ -"
                />
              </div>
              <div className="auth-form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="auth-form-group">
                <label>Geburtsdatum</label>
                <input
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  required
                />
              </div>
              <div className="auth-form-group">
                <label>Passwort</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="8"
                  title="Mindestens 8 Zeichen"
                />
              </div>
            </>
          )}

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-submit-btn" disabled={loading || (mode === 'forgot' && !!success)}>
            {getSubmitLabel()}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'forgot' ? (
            <>
              <button onClick={() => { setMode('login'); clearForm(); }} className="auth-switch-btn">
                ← Zurück zum Login
              </button>
            </>
          ) : mode === 'login' ? (
            <>
              Noch kein Account? 
              <button onClick={switchMode} className="auth-switch-btn">
                Jetzt registrieren
              </button>
            </>
          ) : (
            <>
              Schon registriert? 
              <button onClick={switchMode} className="auth-switch-btn">
                Anmelden
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
